import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import { useAutoResizeTextarea } from './v1_adapter';

import Panel from './Panel';
import ConcernedAboutRevealingModal from './ConcernedAboutRevealingModal';

function RequestDetails({
  details,
  detailsDisplay,
  lookingFor,
  markFieldTouched,
  personType,
  title,
  titleDisplay,
  useUpdateParamWithEvent,
  validationActive,
  validationErrors,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const toggleModal = () => setModalOpen(!modalOpen);

  const requestSummaryLength = (title ?? '').length;
  const detailedDescriptionLength = (details ?? '').length;
  const onPersonTypeChanged = useUpdateParamWithEvent('personType');
  const isRequestSummaryNonEmpty = requestSummaryLength > 0;
  const isDetailedDescriptionNonEmpty = detailedDescriptionLength > 0;

  let titlePlaceholderText = null;
  if (lookingFor === 'product') {
    titlePlaceholderText = "e.g. Seeking Father's Day gift ideas for dads who like the outdoors";
  } else if (personType === 'man_on_the_street') {
    titlePlaceholderText = "e.g. Looking for commuters affected by last week's transit strike";
  } else {
    titlePlaceholderText = 'e.g. Immunologist needed for opinions on booster administration.';
  }

  // Auto-resize textarea with minimum height of 140px
  const descriptionTextareaRef = useAutoResizeTextarea(details, 140);

  return (
    <div>
      <Panel>
        <div>
          {lookingFor === 'person' && (
            <div className="mb-4">
              <p className="mb-2 paragraph-md fw-bold">I&apos;m looking for&hellip;</p>
              <div className="d-flex gap-3">
                <label htmlFor="personTypeExpert" className="d-flex align-items-center gap-2">
                  <input
                    type="radio"
                    id="personTypeExpert"
                    name="personType"
                    className="form-check-input"
                    value="expert"
                    checked={personType === 'expert'}
                    onChange={onPersonTypeChanged}
                  />
                  An expert
                </label>
                <label htmlFor="personTypeMotS" className="d-flex align-items-center gap-2">
                  <input
                    type="radio"
                    id="personTypeMotS"
                    name="personType"
                    className="form-check-input"
                    value="man_on_the_street"
                    checked={personType === 'man_on_the_street'}
                    onChange={onPersonTypeChanged}
                  />
                  A &ldquo;Person on the Street&rdquo;
                  <OverlayTrigger
                    placement="top"
                    overlay={(
                      <Tooltip>
                        Sometimes called a &ldquo;Man on the Street&rdquo; or &ldquo;Vox Pop&rdquo; &mdash;
                        opinions from everyday people, not experts
                      </Tooltip>
                    )}
                  >
                    <i className="ms-1 text-neutral-40 fa fa-info-circle" />
                  </OverlayTrigger>
                </label>
              </div>
            </div>
          )}
          <h4 className="mb-2 paragraph-md fw-semibold">
            {lookingFor === 'product'
              ? 'What type of products are you looking for?'
              : `What kind of ${personType === 'man_on_the_street' ? 'person' : 'expert'} are you looking for?`}
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="right"
              overlay={(
                <Popover>
                  <Popover.Body>
                    Keep titles focused on the expertise needed. Add &ldquo;Urgent&rdquo; at the end if time-sensitive.
                  </Popover.Body>
                </Popover>
              )}
            >
              <i
                className="ms-1 text-neutral-40 fa fa-info-circle"
                style={{ fontSize: '0.85rem', cursor: 'default' }}
              />
            </OverlayTrigger>
          </h4>

          <div
            className={classNames('input-group', {
              'is-invalid': !!validationErrors.title,
              'is-valid': validationActive && !validationErrors.title,
            })}
          >
            <input
              className={classNames('form-control', {
                'pe-1': isRequestSummaryNonEmpty,
                'is-invalid': !!validationErrors.title,
                'is-valid': validationActive && !validationErrors.title,
              })}
              placeholder={titlePlaceholderText}
              value={title}
              onChange={useUpdateParamWithEvent('title')}
              onBlur={() => markFieldTouched('title')}
            />
          </div>

          {validationErrors.title && <div className="invalid-feedback d-block mt-1">{validationErrors.title}</div>}

          <div className="mt-1 d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-link p-0 text-start paragraph-sm text-primary-60"
              onClick={toggleModal}
              style={{ textDecoration: 'none' }}
            >
              Concerned about revealing your story idea?
            </button>
            <div className="paragraph-sm text-neutral-60 text-end">{titleDisplay}</div>
          </div>
        </div>
        <div className="mt-4">
          <h4 className="mb-2 paragraph-md fw-semibold">Add a description</h4>

          <div
            className={classNames('input-group mb-1', {
              'is-invalid': !!validationErrors.details,
              'is-valid': validationActive && !validationErrors.details,
            })}
          >
            <textarea
              ref={descriptionTextareaRef}
              className={classNames('form-control', {
                'pe-1': isDetailedDescriptionNonEmpty,
                'is-invalid': !!validationErrors.details,
                'is-valid': validationActive && !validationErrors.details,
              })}
              placeholder={
                lookingFor === 'product'
                  ? 'Be as specific as possible about the products you want to feature...'
                  : 'Be as specific as possible about the expertise you need...'
              }
              value={details}
              onChange={useUpdateParamWithEvent('details')}
              onBlur={() => markFieldTouched('details')}
              style={{ minHeight: '140px', resize: 'none' }}
            />
          </div>

          {validationErrors.details && <div className="invalid-feedback d-block mt-1">{validationErrors.details}</div>}

          <div className="d-flex justify-content-between mt-2">
            <div className="paragraph-sm text-secondary">
              Tip: A clear and detailed description greatly increases the likelihood of receiving high-quality pitches.
            </div>
            <div className="ms-2 paragraph-sm text-neutral-60 text-end">{detailsDisplay}</div>
          </div>
        </div>
      </Panel>
      <ConcernedAboutRevealingModal isOpen={modalOpen} toggle={toggleModal} />
    </div>
  );
}

RequestDetails.propTypes = {
  details: PropTypes.string,
  detailsDisplay: PropTypes.string,
  lookingFor: PropTypes.string,
  markFieldTouched: PropTypes.func.isRequired,
  personType: PropTypes.string,
  title: PropTypes.string,
  titleDisplay: PropTypes.string,
  useUpdateParamWithEvent: PropTypes.func.isRequired,
  validationActive: PropTypes.bool,
  validationErrors: PropTypes.shape({
    title: PropTypes.string,
    details: PropTypes.string,
  }),
};

RequestDetails.defaultProps = {
  details: '',
  detailsDisplay: '',
  lookingFor: null,
  personType: 'expert',
  title: '',
  titleDisplay: '',
  validationActive: false,
  validationErrors: {},
};

export default RequestDetails;
